import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { applyDesign, renderSite, templates, type DesignedProps } from '@sarnia/shared';
import { api, ApiError } from '../../lib/api';
import { ErrorState } from '../../components/feedback/ErrorState';
import PublicLayout from '../../components/layout/PublicLayout';
import { Icon } from '../../components/ui/Icon';
import './templates.css';

type DesignedPreviewState = {
  status: 'loading' | 'error' | 'success';
  html?: string;
  message?: string;
};

type PreviewDevice = 'desktop' | 'tablet' | 'phone';

export function TemplatePreviewPage() {
  const { templateId } = useParams<{ templateId: string }>();
  const template = templates.find((t) => t.id === templateId);
  const [device, setDevice] = useState<PreviewDevice>('desktop');

  const [designedState, setDesignedState] = useState<DesignedPreviewState>({ status: 'loading' });

  useEffect(() => {
    if (!template || template.kind !== 'designed') {
      setDesignedState({ status: 'loading' });
      return;
    }

    let cancelled = false;
    setDesignedState({ status: 'loading' });

    api
      .get<{ id: string; html: string }>(`/designs/${template.id}`)
      .then((design) => {
        if (!cancelled) {
          setDesignedState({ status: 'success', html: design.html });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setDesignedState({
            status: 'error',
            message: err instanceof ApiError ? err.message : 'Could not load the design preview.',
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [template, templateId]);

  const previewHtml = useMemo(() => {
    if (!template) return '';

    if (template.kind === 'designed') {
      if (designedState.status !== 'success' || !designedState.html) {
        return '';
      }
      const firstBlock = template.site.blocks[0];
      if (!firstBlock) {
        return '';
      }
      if (firstBlock.type !== 'designed') {
        return '';
      }
      const designProps = firstBlock.props as DesignedProps;
      const siteMeta = template.site.meta;
      return applyDesign(designedState.html, designProps, siteMeta);
    }

    const result = renderSite(template.site, {}, {});
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><style>${result.css}</style></head><body>${result.html}</body></html>`;
  }, [template, designedState]);

  if (!template) {
    return (
      <PublicLayout>
        <div className="sarnia-container">
          <ErrorState title="Template not found" message="That template does not exist or may have been removed." />
        </div>
      </PublicLayout>
    );
  }

  const isDesigned = template.kind === 'designed';

  const deviceWidths: Record<PreviewDevice, string> = {
    desktop: '100%',
    tablet: '768px',
    phone: '375px',
  };

  return (
    <PublicLayout>
      <div className="tg-preview">
        <div className="tg-preview__topbar">
          <Link to="/templates" className="sarnia-btn sarnia-btn--ghost sarnia-btn--sm">
            <Icon name="chevron-left" size={16} />
            <span>Back to gallery</span>
          </Link>
          <div className="tg-preview__title">
            <h1>{template.name}</h1>
            <p>{template.description}</p>
          </div>
          <Link to={`/dashboard/sites/new?template=${template.id}`} className="sarnia-btn sarnia-btn--primary">
            Use this template
          </Link>
        </div>

        <div className="tg-device-switcher" role="group" aria-label="Preview device width">
          <button
            type="button"
            className={`tg-device-btn${device === 'desktop' ? ' tg-device-btn--active' : ''}`}
            aria-pressed={device === 'desktop'}
            onClick={() => setDevice('desktop')}
          >
            <Icon name="monitor" size={16} />
            Desktop
          </button>
          <button
            type="button"
            className={`tg-device-btn${device === 'tablet' ? ' tg-device-btn--active' : ''}`}
            aria-pressed={device === 'tablet'}
            onClick={() => setDevice('tablet')}
          >
            <Icon name="tablet" size={16} />
            Tablet
          </button>
          <button
            type="button"
            className={`tg-device-btn${device === 'phone' ? ' tg-device-btn--active' : ''}`}
            aria-pressed={device === 'phone'}
            onClick={() => setDevice('phone')}
          >
            <Icon name="phone" size={16} />
            Phone
          </button>
        </div>

        <div className="tg-preview__frame" style={{ maxWidth: deviceWidths[device] }}>
          {isDesigned ? (
            designedState.status === 'loading' ? (
              <div className="tg-preview__status" role="status">
                Loading preview…
              </div>
            ) : designedState.status === 'error' ? (
              <div className="tg-preview__error" role="alert">
                {designedState.message ?? 'Could not load the design preview.'}
              </div>
            ) : (
              <iframe
                title={`Preview of ${template.name}`}
                srcDoc={previewHtml}
                sandbox="allow-scripts"
                className="tg-preview__iframe"
              />
            )
          ) : (
            <iframe
              title={`Preview of ${template.name}`}
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
              className="tg-preview__iframe"
            />
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
